import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";

import {
  ensureFirebaseAuth,
  firebaseDb,
  isFirebaseConfigured,
  tryEnsureFirebaseAuth,
} from "data/firebase";

/** Admin localStorage koleksiyon adı → Firestore koleksiyon adı */
export const FIRESTORE_COLLECTION_MAP = {
  provinces: "provinces",
  deals: "deals",
  businesses: "businesses",
  bookings: "bookings",
  subscriptions: "subscriptions",
  // Auth `users` ile çakışmasın
  users: "adminUsers",
  supportRequests: "supportRequests",
  paymentPlans: "paymentPlans",
  travelFlights: "travelFlights",
  travelBuses: "travelBuses",
  travelHotels: "travelHotels",
  travelTours: "travelTours",
  travelCars: "travelCars",
  appConfig: "appConfig",
  alerts: "alerts",
  userFavorites: "userFavorites",
  legalDocuments: "legalDocuments",
  sitePages: "sitePages",
};

export const FIREBASE_SYNCED_COLLECTIONS = Object.keys(FIRESTORE_COLLECTION_MAP);

function requireDb() {
  if (!isFirebaseConfigured || !firebaseDb) {
    throw new Error("Firebase yapılandırılmamış.");
  }
  return firebaseDb;
}

function resolveId(item) {
  return String(item?.id || item?.uid || "").trim();
}

export async function loadFirestoreCollection(localName) {
  const remoteName = FIRESTORE_COLLECTION_MAP[localName];
  if (!remoteName) return [];

  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDocs(collection(db, remoteName));
  return snap.docs.map((d) => {
    const data = d.data() || {};
    return { ...data, id: data.id || data.uid || d.id };
  });
}

/**
 * Koleksiyonu Firestore'a yazar.
 * - Listedeki her kaydı set eder
 * - Listede olmayan eski dokümanları siler (hard delete)
 */
export async function saveFirestoreCollection(localName, items) {
  const remoteName = FIRESTORE_COLLECTION_MAP[localName];
  if (!remoteName) return;

  const db = requireDb();
  await ensureFirebaseAuth();

  const colRef = collection(db, remoteName);
  const existing = await getDocs(colRef);
  const nextItems = Array.isArray(items)
    ? JSON.parse(JSON.stringify(items))
    : [];
  const nextIds = new Set(nextItems.map(resolveId).filter(Boolean));

  // Firestore batch max ~500
  const ops = [];

  for (const item of nextItems) {
    const id = resolveId(item);
    if (!id) continue;
    ops.push({ type: "set", id, data: { ...item, id } });
  }

  for (const d of existing.docs) {
    // Başvuru dokümanları (ör. deals içindeki dealApplication) sadece onay ekranından yönetilir;
    // eski bir önbellekle yapılan toplu kayıt onları silmemeli.
    if (!nextIds.has(d.id) && !d.data()?.applicationKind) {
      ops.push({ type: "delete", id: d.id });
    }
  }

  for (let i = 0; i < ops.length; i += 400) {
    const chunk = ops.slice(i, i + 400);
    const batch = writeBatch(db);
    for (const op of chunk) {
      const ref = doc(db, remoteName, op.id);
      if (op.type === "set") batch.set(ref, op.data, { merge: true });
      else batch.delete(ref);
    }
    await batch.commit();
  }
}

/**
 * Yalnızca admin'in değiştirdiği kayıtları yazar. Toplu kayıt, panel açıldıktan sonra
 * mobil/portaldan eklenen ya da güncellenen dokümanları eski önbellekle ezerdi.
 */
export async function saveFirestoreChanges(localName, { upserts = [], deletes = [] } = {}) {
  const remoteName = FIRESTORE_COLLECTION_MAP[localName];
  if (!remoteName || (!upserts.length && !deletes.length)) return;

  const db = requireDb();
  await ensureFirebaseAuth();

  const batch = writeBatch(db);
  for (const item of upserts) {
    const id = resolveId(item);
    if (id) batch.set(doc(db, remoteName, id), { ...item, id }, { merge: true });
  }
  for (const id of deletes) {
    if (id) batch.delete(doc(db, remoteName, String(id)));
  }
  await batch.commit();
}

export async function seedFirestoreCollectionIfEmpty(localName, seedItems) {
  const existing = await loadFirestoreCollection(localName);
  if (existing.length) {
    return { seeded: false, count: existing.length };
  }
  const seed = Array.isArray(seedItems) ? seedItems : [];
  if (!seed.length) {
    return { seeded: false, count: 0 };
  }
  await saveFirestoreCollection(localName, seed);
  return { seeded: true, count: seed.length };
}
